import { useEffect, useRef, useState } from "react";
import { useServers } from "../lib/queries";
import type { Server } from "../lib/types";
import { Icon } from "./icons";
import { SearchInput } from "./SearchInput";
import { cx } from "./ui";

/**
 * Picks one server out of the fleet, or none.
 *
 * A plain `<select>` cannot do this once the fleet outgrows a page: every
 * option has to be in the DOM, so the list silently stops at whatever limit
 * fetched it, and the server an operator wants may simply not be there. This
 * searches instead — the query goes to the API, so what is reachable is the
 * whole fleet rather than the first page of it.
 *
 * The chosen label is held locally rather than read back out of the results,
 * because searching replaces those: pick `fra-1`, type `nyc`, and the selection
 * is no longer in the list it came from. `initialLabel` seeds it for a value
 * that arrived already set, which saves fetching a row we only want a name from.
 */
export function ServerPicker({
  value,
  onChange,
  emptyLabel,
  initialLabel,
  ariaLabel,
  isDisabled,
  describe,
  id,
}: {
  value: string;
  onChange: (serverId: string) => void;
  /** Shown, and offered as the first choice, when nothing is selected. */
  emptyLabel: string;
  /** Name of a preselected server, so opening the picker is not required. */
  initialLabel?: string | null;
  ariaLabel: string;
  isDisabled?: (server: Server) => boolean;
  /** Trailing note on a row — a server's state, say. */
  describe?: (server: Server) => string | null;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [chosenLabel, setChosenLabel] = useState<string | null>(initialLabel ?? null);
  const root = useRef<HTMLDivElement>(null);

  const { data, isPending, error } = useServers({ q, limit: 25 });

  // Dismiss on a click anywhere else. Pointerdown rather than click, so the
  // popover is gone before whatever was clicked underneath reacts.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function choose(serverId: string, label: string | null) {
    onChange(serverId);
    setChosenLabel(label);
    setOpen(false);
    setQ("");
  }

  const servers = data?.data ?? [];
  const truncated = data ? data.total > servers.length : false;

  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        /* Matches the shared CONTROL: `line` at rest, `line-strong` on hover.
           This trigger imitates an input, so it has to imitate its states too. */
        className="flex w-full items-center justify-between gap-2 rounded-md border border-line bg-canvas px-3 py-2 text-left text-sm text-fg transition-colors duration-200 ease-smooth hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none"
      >
        <span className={cx("truncate", value === "" && "text-fg-subtle")}>
          {value === "" ? emptyLabel : (chosenLabel ?? "Selected server")}
        </span>
        <Icon name="chevron" className="size-4 shrink-0 rotate-90 text-fg-subtle" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          // Depth from a hairline and a near-nothing shadow, not a drop shadow:
          // on this canvas anything heavier reads as mud.
          className="absolute z-20 mt-1 w-full min-w-56 border border-line bg-raised shadow-md"
        >
          <div className="border-b border-line p-2">
            <SearchInput value={q} onChange={setQ} placeholder="Search servers…" />
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            <Row selected={value === ""} onClick={() => choose("", null)}>
              <span className="text-fg-subtle">{emptyLabel}</span>
            </Row>

            {isPending ? (
              <p className="px-3 py-2 text-sm text-fg-muted">Loading…</p>
            ) : error ? (
              <p className="px-3 py-2 text-sm text-danger">{error.message}</p>
            ) : servers.length === 0 ? (
              <p className="px-3 py-2 text-sm text-fg-muted">No servers match “{q}”.</p>
            ) : (
              servers.map((server) => {
                const note = describe?.(server) ?? null;
                return (
                  <Row
                    key={server.id}
                    selected={server.id === value}
                    disabled={isDisabled?.(server) ?? false}
                    onClick={() => choose(server.id, server.name)}
                  >
                    <span className="truncate">{server.name}</span>
                    {note && <span className="ml-2 shrink-0 text-fg-subtle">{note}</span>}
                  </Row>
                );
              })
            )}
          </div>

          {/* Searching is the way past this, so say so rather than pretending
              the fleet ends here — the old dropdown gave no such hint. */}
          {truncated && (
            <p className="border-t border-line px-3 py-2 text-xs text-fg-subtle">
              Showing {servers.length} of {data?.total}. Search to narrow.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  selected,
  disabled = false,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "flex w-full items-center px-3 py-1.5 text-left text-sm",
        disabled ? "cursor-not-allowed text-fg-subtle" : "text-fg hover:bg-surface",
        selected && !disabled && "bg-surface font-medium",
      )}
    >
      {children}
      {selected && <Icon name="check" className="ml-auto size-4 shrink-0 text-accent" />}
    </button>
  );
}
